import { api, ApiRes } from './_api-client';
import { AccessType } from './access.service';
import { SetAccessedAtRes, UpdateEntity, UpdateObjectResult } from './project.service';
import { FolderQueryParams } from './folders.service';
import { PaymentType } from './payment.service.ts';

export interface Story {
  storyId: StoryId;
  folderId: FolderId | null;
  projectId: number;
  name: string;
  createdAt: string;
  accessedAt: string | null;
  updatedAt: string | null;
  withFinalcuts: boolean;
  selectedAudioFileId: number | null;
  selectedAudioTrackId: number | null;
  accessType: AccessType;
}

export enum SelectionMethod {
  autoDetect,
  manual,
}

export enum ClipStartTimeAlignment {
  BeatAligned,
  TransitionStart,
}

export enum ClipTransitionAlignment {
  Leading,
  Centered,
  Trailing,
}

export enum AspectRatioStory {
  is16x9,
  is9x16,
  is1x1,
}

export enum ClipOrder {
  chronological,
  maintainFirstLast,
  random,
}

export interface TextPreset {
  fillColor: string;
  shadowColor: string;
  borderColor: string;
  borderWidth: number;
  font: number;
  fontSize: number;
  coordinates: {
    x: number;
    y: number;
  };
}

export interface StoryConfig {
  clipOrder: ClipOrder;
  transitionTime: Milliseconds;
  playbackRate: number;
  selectionMethod: SelectionMethod;
  trackFadeInTime: Milliseconds;
  trackFadeOutTime: Milliseconds;
  titleDuration: Milliseconds;
  creditsDuration: Milliseconds;
  titleText: string;
  creditsText: string;
  titleTextPreset: TextPreset;
  creditsTextPreset: TextPreset;
  startMarkerId: MarkerId | null;
  finishMarkerId: MarkerId | null;
  clipStartTimeAlignment: ClipStartTimeAlignment;
  clipTransitionAlignment: ClipTransitionAlignment;
  aspectRatio: AspectRatioStory;
  resolution: PaymentType;
  snapToBeats: boolean;
}

export interface CreateStoryBody {
  projectId: number;
  name: string;
}

export interface CopyStoryBody {
  storyId: number;
  name: string;
}

export interface SetStoryConfigBody extends StoryConfig {
  storyId: StoryId;
}

/* S E R V I C E */

/* Stories */

export const getProjectStories = (projectId: ProjectId, { folderId, allActive }: FolderQueryParams) => {
  const params = new URLSearchParams({ projectId: String(projectId) });
  if (folderId !== undefined) params.append('folderId', String(folderId));
  if (allActive !== undefined) params.append('allActive', String(allActive));
  return api.get<Story[]>(`/Story/GetProjectStories?${params.toString()}`);
};

export const createStory = (body: CreateStoryBody) => {
  return api.post<Story>(`/Story/CreateStory`, body);
};

export const copyStory = (body: CopyStoryBody) => {
  return api.post<Story>(`/Story/CopyStory?storyId=${body.storyId}&storyName=${encodeURIComponent(body.name)}`);
};

export const updateStory = (payload: UpdateEntity) => {
  return api.patch<UpdateObjectResult>('/Story/updateStory', { name: payload.name, storyId: payload.id });
};

export const removeStory = (storyId: StoryId) => {
  return api.delete<boolean>(`/Story/RemoveStory?storyId=${storyId}`);
};

export const setStoryAccessedAt = (storyId: StoryId) => {
  return api.patch<SetAccessedAtRes>(`/Story/SetStoryAccessedAt?storyId=${storyId}`);
};

export const restoreFinalCutStory = (finalcutId: FinalcutId, storyName: string) => {
  return api.post<Story>(`/Story/RestoreStory?finalCutId=${finalcutId}&storyName=${encodeURIComponent(storyName)}`);
};

/* Story Config */

export const setStoryConfig = (body: SetStoryConfigBody) => {
  return api.put<ApiRes>(`/Story/SetStoryConfig`, body);
};

export const getStoryConfig = (storyId: StoryId) => {
  return api.get<StoryConfig>(`/Story/GetStoryConfig?storyId=${storyId}`);
};
